/**
 * training-class-view.js
 */

import React from 'react';
import { useMediaQuery, Grid, Typography, IconButton } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import {
  useHistory,
  useParams,
} from "react-router-dom";
import { Types } from 'utils/constants';
import { makeLocation } from 'utils/routing';
import { format, parseISO } from 'date-fns';
import useRecordStore from 'contexts/record-store';
import DrawerContentBuilder from 'components/menu-drawer/drawer-content-builder';
import DrawerContent from 'components/menu-drawer/drawer-content';
import EditButton from 'components/edit-button';
import RejectionAlert from 'components/rejection-alert';
import { UserContext } from 'contexts/user/user-context';
import MobilePageTitle from 'components/menu-drawer/mobile-page-title';
import Icon from 'components/icon';
import IconSection from 'components/icon-section';
import FilesView from 'components/files/view';
import ReviewToggle from 'components/review-toggle';
import RecordFooter from 'components/record-footer';


const useStyles = makeStyles(theme => ({
  locationContainer: {
    maxWidth: 450,
    [theme.size.menuDrawer.mediaRuleMobileContent]: {
      maxWidth: 'none'
    }
  },
  mapContainer: {
    display: 'flex', 
    marginBottom: theme.spacing(3),
    [theme.size.menuDrawer.mediaRuleMobileContent]: {
      marginTop: theme.spacing(-4),
      marginLeft: theme.spacing(5),
    }
  },
  memberList: {
    '& .row': {
      display: 'flex',
      margin: `3px ${theme.spacing(1)}`,
      marginLeft: 0,
    },
    '& .header': {
      color: theme.palette.grey.colors[6],
      marginBottom: 4,
    },
    '& .attendance': {
      display: 'flex',
      marginRight: theme.spacing(1)
    },
  },
  reviewContainer: {
    justifyContent: 'flex-end',
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    }
  }
}));


// use wrapper to remount component when class ID changes
export default function TrainingClassView(props) {
  const { classId } = useParams();

  return <TrainingClassViewImplementation
    classId={classId}
    key={classId}
    {...props}
  />;
}


function TrainingClassViewImplementation(props) {
  const {
    classId,
    drawerState,
    onClose,
    basePath
  } = props;
  const classes = useStyles();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  let history = useHistory();
  const { user } = React.useContext(UserContext);
  const recordStore = useRecordStore();
  const classRecord = recordStore.useRecord(Types.Record.CLASS, classId).ornull;
  const hasRecord = Boolean(classRecord);
  const isComplete = hasRecord ? classRecord.get().isComplete : false;


  React.useEffect(() => {
    setTimeout(() => drawerState.loading.content.set(!hasRecord));

  // drawerState is hookstate setter
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasRecord]);


  const closeButton = (
    <IconButton
      data-testid="mobile-drawer-close-button"
      size="small"
      style={{color: 'inherit'}}
      onClick={onClose}
      children={<Icon type='mobile-close-drawer' />}
    />
  );


  const getEditButton = (isMobile) => !user.role.isSupervisor ? (
    <EditButton 
      disabled={!user.role.status.isActive}
      data-testid='edit-button'
      iconOnly={isMobile}
      to={makeLocation(
        basePath,
        'class',
        'edit',
        {classId},
        history.location
      )}
    />
  ) : null;


  const reviewButtons = (isComplete && user.role.isSupervisor) ? (
    <div className={`flex-hc ${classes.reviewContainer}`}>
      <ReviewToggle
        type={Types.Record.CLASS}
        id={classId}
        drawerState={drawerState}
      />
    </div>
  ) : null;


  const getReviewedStatus = () => {
    const reviewer = classRecord.get()?.review?.reviewer?.name;
    const reviewStatus = classRecord.get()?.review?.status
    return reviewStatus === Types.ReviewStatus.ACCEPTED
      ? `Yes, by ${reviewer}`
      : reviewStatus === Types.ReviewStatus.REJECTED
        ? `Rejected, by ${reviewer}`
        : "No";
  }


  return (
    <>
      {classRecord &&
      <DrawerContentBuilder
        mobileAppBar={{
          title: 'Class',
          rightActions: <>{closeButton}{getEditButton(true)}</>,
          recordGroupType: Types.RecordGroup.CLASS,
        }}
        desktopHeader={{
          title: classRecord.get().name,
          actions: <>{getEditButton(false)}{reviewButtons}</>
        }}
        stickyHeader
        children={<>
          <MobilePageTitle title={classRecord.get().name} />
          <DrawerContent>
            {isMobile ? reviewButtons : null}
            <RejectionAlert review={classRecord.get().review} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={8} className={classes.locationContainer}>

                {/* main */}
                <IconSection iconName='summary'>
                  <Typography variant="body1">
                    {classRecord.get().location || 'No location provided'}
                  </Typography>
                  {classRecord.get().instructor &&
                  <Typography variant="body1">
                    {`Instructor: ${classRecord.get().instructor}`}
                  </Typography>}
                  <Typography variant="body1">
                    {`Report Reviewed: ${getReviewedStatus()}`}
                  </Typography>
                </IconSection>

                {/* datetime & duration */}
                <IconSection iconName='event'>
                  <Typography variant="body1">
                    {format(parseISO(classRecord.get().date), 'MMM dd, yyyy h:mm aaa')}
                  </Typography>
                  <Typography>
                    {classRecord.get().duration} Hour{`${classRecord.get().duration === 1 ? '' : 's'}`}
                  </Typography>
                </IconSection>
             
              </Grid>
            </Grid>

            {/* notes */}
            {classRecord.get().notes &&
            <IconSection iconName='comment'>
              <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                {classRecord.get().notes}
              </Typography>
            </IconSection>}

            {/* files */}
            <FilesView files={classRecord.get().files} />

            <RecordFooter
              createdBy={classRecord.get().createdBy}
              dateCreated={classRecord.get()?.dateCreated}
            />

          </DrawerContent>   
        </>}
      />}
    </>
  );
}